import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { BlogReadingProgress } from "@/components/BlogReadingProgress";
import { BlogTableOfContents } from "@/components/BlogTableOfContents";
import { Link } from "react-router-dom";
import { Timer, Brain, RefreshCw, Clock, Zap, Users, ArrowRight, ArrowLeft, Target, Sparkles, Heart, Smartphone } from "lucide-react";
import { Helmet } from "react-helmet-async";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const tocItems = [
  { id: "learning-in-cycles", title: "Your Brain Learns in Cycles" },
  { id: "pushing-through", title: "Why Pushing Through Slows You Down" },
  { id: "working-memory", title: "Breaks Protect Working Memory" },
  { id: "memory-consolidation", title: "Breaks Help Memory Consolidation" },
  { id: "scrolling-bad", title: "Why Scrolling Is a Bad Break" },
  { id: "stress-reduction", title: "Breaks Reduce Stress" },
  { id: "shared-breaks", title: "Breaks Work Better in Groups" },
  { id: "csw-breaks", title: "Where CSW Fits In" },
  { id: "faq", title: "FAQ" },
];

const WhyBreaksMatter = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why are breaks important in the Pomodoro Technique?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Breaks aren't just rewards — they're part of the learning process. Research shows that attention naturally declines over time (vigilance decrement), and breaks reset this decline before it becomes costly. Without breaks, your brain absorbs information less efficiently and makes more mistakes."
        }
      },
      {
        "@type": "Question",
        "name": "Does pushing through without breaks help you study more?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Research from the University of Illinois found that brief diversions from a task dramatically improve focus and accuracy when people return. Without breaks, your brain habituates and stops responding as strongly to the task."
        }
      },
      {
        "@type": "Question",
        "name": "How do breaks help with memory?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Learning doesn't only happen during studying — it also happens between sessions. Neuroscience research shows that short rest periods help the brain consolidate new information, strengthening memory traces formed during study."
        }
      },
      {
        "@type": "Question",
        "name": "Why is scrolling on my phone a bad break?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Passive scrolling floods attention with new stimuli, keeps working memory active, and prevents mental recovery. Research shows attention doesn't fully reset when breaks are filled with fast-paced screen content. Better breaks involve movement, hydration, or looking away from screens."
        }
      },
      {
        "@type": "Question",
        "name": "How do breaks reduce stress?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Stress builds when effort feels endless. Research on recovery and stress regulation shows that predictable breaks reduce anxiety more effectively than unplanned rest. Pomodoro makes rest expected — not something you have to justify."
        }
      },
      {
        "@type": "Question",
        "name": "Why do breaks work better in shared study sessions?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Breaks are easier to respect when shared. In study streams, everyone stops together and returning feels natural. Research on synchronized group behavior shows that shared rhythms improve adherence and reduce burnout."
        }
      }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Why Breaks Make You Smarter: The Psychology Behind Pomodoro Breaks",
    "description": "Learn why Pomodoro breaks are essential for learning, memory consolidation, and sustained focus. Discover why skipping breaks backfires.",
    "image": "https://csw.live/csw-logo-new.png",
    "author": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "publisher": {
      "@type": "Organization",
      "name": "CSW - Collaborative Study Workspace",
      "logo": {
        "@type": "ImageObject",
        "url": "https://csw.live/csw-logo-new.png"
      }
    },
    "datePublished": "2025-01-18",
    "dateModified": "2025-01-18",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://csw.live/pomodoro-technique/why-breaks-matter/"
    },
    "keywords": ["pomodoro technique", "pomodoro breaks", "study breaks", "memory consolidation", "focus recovery", "vigilance decrement", "working memory", "productivity", "study stream", "online workspace", "virtual study room", "focus timer", "rest and recovery", "cognitive restoration", "attention reset", "study with me", "online study platform"]
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Why Breaks Make You Smarter: The Psychology Behind Pomodoro Breaks | CSW",
    "description": "Learn why Pomodoro breaks are essential for learning, memory consolidation, and sustained focus. Discover why skipping breaks backfires.",
    "url": "https://csw.live/pomodoro-technique/why-breaks-matter/",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CSW - Collaborative Study Workspace",
      "url": "https://csw.live"
    },
    "about": {
      "@type": "Thing",
      "name": "Study Breaks"
    },
    "mentions": [
      { "@type": "Thing", "name": "Pomodoro Technique" },
      { "@type": "Thing", "name": "Memory Consolidation" },
      { "@type": "Thing", "name": "Working Memory" },
      { "@type": "Thing", "name": "Study Stream" },
      { "@type": "Thing", "name": "Online Workspace" },
      { "@type": "Thing", "name": "Virtual Study Room" },
      { "@type": "Thing", "name": "Focus Timer" },
      { "@type": "Thing", "name": "Productivity" },
      { "@type": "Thing", "name": "Vigilance Decrement" },
      { "@type": "Thing", "name": "Cognitive Restoration" },
      { "@type": "Thing", "name": "Attention Reset" },
      { "@type": "Thing", "name": "Rest and Recovery" }
    ],
    "keywords": "pomodoro technique, pomodoro breaks, study breaks, memory consolidation, focus recovery, working memory, productivity, study stream, online workspace, virtual study room, focus timer, cognitive restoration, attention reset"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://csw.live/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Pomodoro Technique",
        "item": "https://csw.live/pomodoro-technique/"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Why Breaks Matter",
        "item": "https://csw.live/pomodoro-technique/why-breaks-matter/"
      }
    ]
  };

  const relatedArticles = [
    {
      title: "The Science of Focus",
      path: "/pomodoro-technique/science-of-focus/",
      description: "Why 25 minutes is enough (most of the time)"
    },
    {
      title: "Studying Without Burning Out",
      path: "/pomodoro-technique/avoid-burnout/",
      description: "How Pomodoro helps manage stress"
    },
    {
      title: "Customize Your Pomodoro",
      path: "/pomodoro-technique/customize/",
      description: "Finding the right session length for you"
    },
    {
      title: "Pomodoro and Study Streams",
      path: "/pomodoro-technique/study-streams/",
      description: "Why studying together quietly improves focus"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
      </Helmet>
      <SEO 
        title="Why Breaks Make You Smarter: The Psychology Behind Pomodoro Breaks | CSW"
        description="Learn why Pomodoro breaks are essential for learning, memory consolidation, and sustained focus. Discover why skipping breaks backfires."
        canonicalUrl="/pomodoro-technique/why-breaks-matter/"
      />
      <Header />
      <BlogReadingProgress />
      
      <main className="min-h-screen bg-background">
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
          
          <div className="container relative z-10 px-4 md:px-6">
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/pomodoro-technique/" className="text-muted-foreground hover:text-primary transition-colors">Pomodoro Technique</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-foreground font-medium">Why Breaks Matter</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
                <RefreshCw className="w-4 h-4" />
                <span>Break Science</span>
              </div>
              
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Why Breaks Make You{" "}
                <span className="text-primary">Smarter</span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                The psychology behind Pomodoro breaks (and why skipping them backfires).
              </p>
            </div>
          </div>
        </section>

        <article className="pb-24 relative">
          <div className="container mx-auto px-4">
            <div className="flex gap-12 max-w-6xl mx-auto">
              <aside className="hidden lg:block w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <BlogTableOfContents items={tocItems} />
                </div>
              </aside>

              <div className="flex-1 max-w-3xl">
                <div className="lg:hidden mb-12">
                  <BlogTableOfContents items={tocItems} />
                </div>

                <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed font-body mb-10">
                  Most students treat breaks like this: "I'll take one when I'm done."
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  The problem? Being "done" rarely happens — and by the time you finally stop, your brain is already fried.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  The Pomodoro Technique flips that thinking completely.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Breaks aren't a reward. They're <strong>part of the learning process</strong>. And research strongly supports this idea.
                </p>

                {/* Learning in Cycles */}
                <div id="learning-in-cycles" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Brain className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Your Brain Doesn't Learn Continuously — It Learns in Cycles
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Studying feels like it should be continuous. But cognitively, it isn't.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research shows that <strong>attention and performance naturally decline over time</strong>, even when people are motivated. This is known as <em>vigilance decrement</em> (
                  <a href="https://journals.sagepub.com/doi/10.1177/0018720808318025" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Helton & Warm, 2008</a>). Learn more about this phenomenon in our guide to <Link to="/pomodoro-technique/science-of-focus/" className="text-primary hover:underline">the science of focus</Link>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Without breaks, your brain:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>absorbs information less efficiently</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>makes more mistakes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>requires more time for the same work</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Breaks reset this decline before it becomes costly.
                  </p>
                </div>

                {/* Why Pushing Through Slows You Down */}
                <div id="pushing-through" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Zap className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why "Pushing Through" Actually Slows You Down
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Many students believe working longer without breaks means getting more done.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  In reality, studies show the opposite.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research from the University of Illinois found that <strong>brief diversions from a task dramatically improve focus and accuracy</strong> when people return to it (
                  <a href="https://www.sciencedirect.com/science/article/abs/pii/S0010027710002994" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ariga & Lleras, 2011</a>).
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  When you don't step away, your brain habituates — it literally stops responding as strongly to the task.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Breaks interrupt that habituation.
                  </p>
                </div>

                {/* Breaks Protect Working Memory */}
                <div id="working-memory" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Target className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Short Breaks Protect Working Memory
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Working memory — the mental space where you hold and manipulate information — is limited.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Cognitive load theory shows that <strong>overloading working memory reduces learning and comprehension</strong>, especially in complex subjects.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro breaks:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>unload working memory</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>reduce mental clutter</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>prepare your brain for the next session</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    That's why studying feels "lighter" after a break.
                  </p>
                </div>

                {/* Memory Consolidation */}
                <div id="memory-consolidation" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Breaks Help With Memory Consolidation
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Learning doesn't only happen during studying. It also happens <strong>between sessions</strong>.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Neuroscience research shows that short rest periods help the brain consolidate new information — strengthening memory traces formed during study (
                  <a href="https://www.pnas.org/doi/10.1073/pnas.1108920108" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Dewar et al., 2012</a>).
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  In other words, breaks don't interrupt learning.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    They <strong>complete it</strong>.
                  </p>
                </div>

                {/* Why Scrolling Is a Bad Break */}
                <div id="scrolling-bad" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Scrolling Is a Bad Break (Most of the Time)
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Not all breaks are equal.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Passive scrolling:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>floods attention with new stimuli</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>keeps working memory active</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>prevents mental recovery</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on digital distraction shows that <strong>attention doesn't fully reset</strong> when breaks are filled with fast-paced screen content.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Better Pomodoro breaks involve:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>movement</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>hydration</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>stretching</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>looking away from screens</span>
                  </li>
                </ul>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    Simple resets work best.
                  </p>
                </div>

                {/* Breaks Reduce Stress */}
                <div id="stress-reduction" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Heart className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Breaks Reduce Stress — Not Just Fatigue
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Stress builds when effort feels endless.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on recovery and stress regulation shows that <strong>predictable breaks reduce anxiety</strong> more effectively than unplanned rest.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Pomodoro makes rest expected — not something you have to justify.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    That psychological safety matters.
                  </p>
                </div>

                {/* Breaks Work Better in Groups */}
                <div id="shared-breaks" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Users className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Why Breaks Work Even Better in Shared Study Sessions
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Breaks are easier to respect when they're shared.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  In study streams and virtual study rooms:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>everyone stops together</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>returning feels natural</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-primary">•</span>
                    <span>overworking feels unnecessary</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  Research on synchronized group behavior shows that <strong>shared rhythms improve adherence and reduce burnout</strong>.
                </p>

                <div className="bg-muted/50 rounded-xl p-6 mb-12">
                  <p className="text-lg font-body text-foreground/90 italic text-center m-0">
                    This is why Pomodoro-based study rooms are so sustainable.
                  </p>
                </div>

                {/* Where CSW Fits In */}
                <div id="csw-breaks" className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Timer className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 tracking-tight">
                        Where CSW Supports Healthy Breaks
                      </h2>
                    </div>
                  </div>
                </div>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  This is where <strong>csw.live</strong> fits naturally.
                </p>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-8">
                  CSW:
                </p>

                <ul className="space-y-3 mb-8 text-foreground/90 font-body">
                  <li className="flex items-start gap-3">
                    <Timer className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>enforces break timing</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <RefreshCw className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>normalizes rest</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>prevents "just one more minute" burnout</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                    <span>keeps sessions sustainable</span>
                  </li>
                </ul>

                <p className="text-lg leading-relaxed font-body text-foreground/90 mb-12">
                  Instead of relying on self-control, the environment does the work.
                </p>

                {/* Final Takeaway */}
                <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl p-8 mb-12">
                  <h3 className="text-xl font-display font-bold mb-4">Final Takeaway</h3>
                  <p className="text-lg font-body text-foreground/90 mb-0">
                    Breaks don't steal time from studying. They <strong>protect it</strong>. Pomodoro works because it respects the brain's limits — and learning happens best when effort and rest work together. Skipping breaks doesn't make you stronger. It makes studying harder than it needs to be.
                  </p>
                </div>

                {/* FAQ Section */}
                <div id="faq" className="bg-card border border-border rounded-2xl p-8 mb-10 scroll-mt-28">
                  <div className="flex items-start gap-6 mb-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight">
                        Frequently Asked Questions
                      </h2>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    {faqSchema.mainEntity.map((faq, index) => (
                      <div key={index} className="border-b border-border pb-6 last:border-0 last:pb-0">
                        <h3 className="text-lg font-display font-semibold mb-2">{faq.name}</h3>
                        <p className="text-foreground/80 font-body">{faq.acceptedAnswer.text}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center py-8 border-t border-border">
                  <Link 
                    to="/pomodoro-technique/" 
                    className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Back to Pomodoro Technique</span>
                  </Link>
                  <Link 
                    to="/study-timer/" 
                    className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full hover:bg-primary/90 transition-colors"
                  >
                    <span>Try the Pomodoro Timer</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Related Articles */}
                <div className="mt-16">
                  <h2 className="text-2xl font-display font-bold mb-8">Related Articles</h2>
                  <div className="grid gap-4">
                    {relatedArticles.map((article, index) => (
                      <Link 
                        key={index}
                        to={article.path}
                        className="group p-6 bg-card border border-border rounded-xl hover:border-primary/30 transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mt-1">{article.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Study Stream Resources */}
                <div className="mt-12 p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20 rounded-2xl">
                  <h2 className="text-2xl font-display font-bold mb-3">Take Better Breaks Inside a Study Stream</h2>
                  <p className="text-muted-foreground mb-6">
                    Shared 5-minute breaks turn a solo Pomodoro into a community ritual. Join a free <Link to="/study-stream/" className="text-primary hover:underline">Study Stream</Link>:
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Link to="/study-stream/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Free 24/7 Study Stream with Shared Breaks</div>
                      <div className="text-sm text-muted-foreground">Live focus rooms with synchronized rest cycles</div>
                    </Link>
                    <Link to="/study-stream/studying-alone-together/" className="p-4 bg-background/60 border border-border rounded-xl hover:border-primary/40 transition-colors">
                      <div className="font-semibold text-foreground">Studying Alone, Together</div>
                      <div className="text-sm text-muted-foreground">Why community breaks beat solo scrolling</div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      
      <Footer />
    </div>
  );
};

export default WhyBreaksMatter;
